import React, { useEffect, useMemo, useState } from 'react';
import SeoHead from '../components/SeoHead';
import AgentSelector from '../components/AgentSelector';
import AgentChat from '../components/AgentChat';
import { PublicClientApplication } from '@azure/msal-browser';

const boxStyle = {
  background: '#fff',
  border: '1px solid #F0DFDB',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 8px 18px rgba(0,0,0,0.05)',
};

const msButtonStyle = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #C03626',
  background: '#FFF4F1',
  color: '#C03626',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const getUsername = () => localStorage.getItem('username') || '';

const EmailAgent = () => {
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [seedHistory, setSeedHistory] = useState(null);
  const [emailContext, setEmailContext] = useState('');
  const [msConfig, setMsConfig] = useState(null);
  const [msConfigError, setMsConfigError] = useState('');
  const [msLoading, setMsLoading] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [connectedAt, setConnectedAt] = useState(null);
  const [msalReady, setMsalReady] = useState(false);
  const username = getUsername();

  const openEmailChat = (email) => {
    if (!email) return;
    setSelectedEmail(email);
    const session = email.id ? `email-${email.id}` : `email-${Date.now()}`;
    setChatSessionId(session);
    const stripHtml = (html) => {
      if (!html) return '';
      return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    };
    const bodyText = stripHtml(email.body) || email.summary || email.bodyPreview || 'No summary provided.';
    const context = `Email context (keep this in every answer):\nSubject: ${email.subject || 'No subject'}\nFrom: ${email.fromName || email.from || 'Unknown sender'}\nReceived: ${email.receivedDateTime ? new Date(email.receivedDateTime).toLocaleString() : 'Unknown time'}\nBody:\n${bodyText}`;
    setEmailContext(context);
    setSeedHistory({
      module: 'default',
      messages: [
        { role: 'assistant', content: '📧 Jeg har indlæst denne e-mail. Stil spørgsmål, bed om et svarudkast eller næste skridt.' },
        { role: 'user', content: context },
      ],
    });
  };

  useEffect(() => {
    const loadMsConfig = async () => {
      setMsConfigError('');
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/ms/config`, { credentials: 'include' });
        const json = await res.json().catch(() => null);
        if (!res.ok) {
          setMsConfig(null);
          setMsConfigError(json?.message || 'Microsoft configuration not available.');
          return;
        }
        if (json?.clientId && json?.tenantId) {
          setMsConfig({ clientId: json.clientId, tenantId: json.tenantId });
        } else {
          setMsConfig(null);
          setMsConfigError('Microsoft configuration is incomplete.');
        }
      } catch (err) {
        console.error('Failed to load Microsoft config', err);
        setMsConfig(null);
        setMsConfigError('Microsoft configuration not available.');
      }
    };

    const loadConnection = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/ms/connection`, { credentials: 'include' });
        const json = await res.json().catch(() => null);
        if (res.ok) {
          setConnectedEmail(json?.outlookEmail || '');
          setConnectedAt(json?.outlookConnectedAt || null);
        }
      } catch (err) {
        console.error('Failed to load Microsoft connection status', err);
      }
    };

    loadMsConfig();
    loadConnection();
  }, []);

  const msalInstance = useMemo(() => {
    if (!msConfig?.clientId || !msConfig?.tenantId) return null;
    return new PublicClientApplication({
      auth: {
        clientId: msConfig.clientId,
        authority: `https://login.microsoftonline.com/${msConfig.tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    });
  }, [msConfig]);

  useEffect(() => {
    let active = true;
    const initMsal = async () => {
      if (!msalInstance) {
        setMsalReady(false);
        return;
      }
      try {
        await msalInstance.initialize();
        if (active) setMsalReady(true);
      } catch (err) {
        console.error('MSAL initialize failed', err);
        if (active) setMsalReady(false);
      }
    };
    initMsal();
    return () => { active = false; };
  }, [msalInstance]);

  const handleMicrosoftLogin = async () => {
    if (!msalInstance || !msalReady) return;
    setMsLoading(true);
    setError('');
    try {
      const loginRequest = {
        scopes: ['openid', 'profile', 'email', 'User.Read', 'Mail.ReadWrite'],
        prompt: 'select_account',
      };
      const response = await msalInstance.loginPopup(loginRequest);
      const account = response?.account || msalInstance.getAllAccounts()?.[0];
      const email = account?.username || '';
      if (email) {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const saveRes = await fetch(`${apiBase}/api/ms/connection`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ outlookEmail: email }),
        });
        const saveJson = await saveRes.json().catch(() => null);
        if (saveRes.ok) {
          setConnectedEmail(saveJson?.outlookEmail || email);
          setConnectedAt(saveJson?.outlookConnectedAt || new Date().toISOString());
        } else {
          setError(saveJson?.message || 'Failed to save Microsoft connection.');
        }
      }
    } catch (err) {
      console.error('Microsoft login failed', err);
      setError('Microsoft login failed. Please try again.');
    } finally {
      setMsLoading(false);
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <SeoHead
        title="Email Agent | AICE"
        description="Automatiseret e-mail agent."
        canonicalPath="/agents/email"
      />
      <div style={{ marginBottom: '0.75rem' }}>
        <AgentSelector />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>
            Welcome {username ? username : 'there'}, what can I help with today?
          </h2>
          <p style={{ marginTop: 8, color: '#5E514D' }}>
            Connect Outlook to enable email drafting in the next step.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={!msalInstance || !msalReady || msLoading}
            style={{
              ...msButtonStyle,
              opacity: !msalInstance || !msalReady || msLoading ? 0.6 : 1,
              cursor: !msalInstance || !msalReady || msLoading ? 'not-allowed' : 'pointer',
            }}
            title={!msalInstance ? (msConfigError || 'Microsoft configuration missing') : (!msalReady ? 'Microsoft auth initializing' : 'Connect Outlook')}
          >
            {msLoading ? 'Connecting…' : (connectedEmail ? 'Change user' : 'Add new user')}
          </button>
          {connectedEmail && (
            <div style={{ fontSize: 12, color: '#5E514D' }}>
              Connected: {connectedEmail}{connectedAt ? ` · ${new Date(connectedAt).toLocaleDateString()}` : ''}
            </div>
          )}
          {!connectedEmail && msConfigError && (
            <div style={{ fontSize: 12, color: '#B3261E', maxWidth: 260, textAlign: 'right' }}>{msConfigError}</div>
          )}
        </div>
      </div>

      {error && (
        <div style={{ ...boxStyle, borderColor: '#F6C7C2', background: '#FFF5F3', color: '#B3261E', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {selectedEmail && (
        <div style={{ marginTop: 20 }}>
          <div style={{ ...boxStyle, background: '#FFF9F7', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, color: '#C03626' }}>Chat about: {selectedEmail.subject || 'No subject'}</div>
            <div style={{ fontSize: 13, color: '#5E514D', marginTop: 6 }}>
              From: {selectedEmail.fromName || selectedEmail.from || 'Unknown sender'} ·{' '}
              {selectedEmail.receivedDateTime ? new Date(selectedEmail.receivedDateTime).toLocaleString() : 'No time'}
            </div>
          </div>
          <AgentChat
            key={chatSessionId || 'email-chat'}
            moduleName="emailAgent"
            sessionId={chatSessionId}
            setSessionId={setChatSessionId}
            preservedHistory={seedHistory}
            emailContext={emailContext}
          />
        </div>
      )}
    </div>
  );
};

export default EmailAgent;
